import type { DisplayDescriptor } from '../../platform/display-descriptors';
import { PROBE_TYPE_OPTIONS } from './constants';
import { healthCheckFromLivenessProbe, isHealthCheckEnabled } from './healthCheckForm';
import type { HealthCheckFormValues, ProbeType } from './types';

function previewRow(id: string, group: string, label: string, value: unknown): DisplayDescriptor | undefined {
  if (value === undefined || value === null || String(value).trim() === '') return undefined;
  return { id, group, label, value: String(value) };
}

function resolveProbeTypeLabel(probeType?: ProbeType | string): string | undefined {
  const normalized = String(probeType ?? '')
    .trim()
    .toLowerCase() as ProbeType;
  if (!normalized) return undefined;
  return PROBE_TYPE_OPTIONS.find((item) => item.value === normalized)?.label ?? normalized;
}

/** 从表单探活值生成确认预览行 */
export function buildHealthCheckDisplayDescriptorsFromForm(
  healthCheck: HealthCheckFormValues | undefined,
  group = 'healthCheck',
): DisplayDescriptor[] {
  if (!isHealthCheckEnabled(healthCheck) || !healthCheck) return [];

  const probeType = String(healthCheck.defaultProbeType ?? 'http')
    .trim()
    .toLowerCase() as ProbeType;
  const rows: (DisplayDescriptor | undefined)[] = [
    previewRow('healthCheck.probeType', group, '默认探活类型', resolveProbeTypeLabel(probeType)),
  ];

  if (probeType === 'http' || probeType === 'tcp') {
    rows.push(previewRow('healthCheck.port', group, '默认端口', healthCheck.defaultPort));
  }
  if (probeType === 'http') {
    rows.push(
      previewRow('healthCheck.path', group, '默认路径', healthCheck.defaultPath),
      previewRow('healthCheck.httpMethod', group, 'HTTP 请求方式', healthCheck.httpMethod || 'GET'),
    );
  }
  if (probeType === 'exec') {
    rows.push(previewRow('healthCheck.command', group, '默认执行命令', healthCheck.defaultCommand));
  }

  rows.push(
    previewRow('healthCheck.initialDelaySeconds', group, '启动等待时间 (秒)', healthCheck.initialDelaySeconds),
    previewRow('healthCheck.periodSeconds', group, '检查间隔 (秒)', healthCheck.periodSeconds),
    previewRow('healthCheck.timeoutSeconds', group, '超时时间 (秒)', healthCheck.timeoutSeconds),
    previewRow('healthCheck.failureThreshold', group, '失败阈值', healthCheck.failureThreshold),
  );

  return rows.filter((item): item is DisplayDescriptor => Boolean(item));
}

/** 从 K8s livenessProbe 生成确认预览行（fromForm / API 真源） */
export function buildHealthCheckDisplayDescriptorsFromLivenessProbe(
  probe: Record<string, unknown> | undefined,
  group = 'healthCheck',
): DisplayDescriptor[] {
  return buildHealthCheckDisplayDescriptorsFromForm(healthCheckFromLivenessProbe(probe), group);
}
